// src/components/SearchSuggestions.jsx

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { fetchSuggestions } from '../services/api';
import './SearchSuggestions.css';
import { Star } from 'lucide-react';

const SUGGESTION_ITEM_HEIGHT = 64;

const SearchSuggestions = ({
  query,
  onSelect,
  visible,
  onClose,
  inputRef
}) => {
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const suggestionsRef = useRef(null);

  // We'll track the last fetched query so we don't show "No results" for old queries
  const [lastFetchedQuery, setLastFetchedQuery] = useState('');

  // This ref helps us discard stale fetch results
  const currentQueryRef = useRef('');

  // Separate suggestions — upcoming movies appear alongside released ones
  const movieSuggestions = allSuggestions.filter(item => item.type === 'movie' || item.type === 'upcoming');
  const peopleSuggestions = allSuggestions.filter(item => item.type !== 'movie' && item.type !== 'upcoming');

  // Flatten for arrow keys
  const flattenedList = useMemo(
    () => [...movieSuggestions, ...peopleSuggestions],
    [movieSuggestions, peopleSuggestions]
  );

  // When query changes, reset selection
  useEffect(() => {
    setSelectedIndex(-1);
  }, [query]);

  // If user typed <2 chars, clear suggestions (no fetch)
  useEffect(() => {
    // Only proceed with fetching if query length is at least 2
    if (!query || query.length < 2) {
      return;
    }
    const controller = new AbortController();
    let cancel = false;

    const queryForThisFetch = query;
    currentQueryRef.current = queryForThisFetch;

    async function doFetch() {
      setLoading(true);
      try {
        const resp = await fetchSuggestions(
          { query: queryForThisFetch },
          { signal: controller.signal }
        );
        if (!cancel && resp?.data) {
          if (currentQueryRef.current === queryForThisFetch) {
            setAllSuggestions(resp.data);
            setLastFetchedQuery(queryForThisFetch);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Suggestions fetch error:', err);
          if (currentQueryRef.current === queryForThisFetch) {
            setAllSuggestions([]);
            setLastFetchedQuery(queryForThisFetch);
          }
        }
      } finally {
        if (!cancel && currentQueryRef.current === queryForThisFetch) {
          setLoading(false);
        }
      }
    }

    const timerId = setTimeout(doFetch, 200);
    return () => {
      clearTimeout(timerId);
      controller.abort();
      cancel = true;
    };
  }, [query]);

  // Keydown events
  const handleKeyDown = useCallback(
    e => {
      if (!visible || loading || flattenedList.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < flattenedList.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : -1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            onSelect(flattenedList[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    },
    [visible, loading, flattenedList, selectedIndex, onSelect, onClose]
  );

  useEffect(() => {
    const currentInput = inputRef.current;
    if (!currentInput) return;

    currentInput.addEventListener('keydown', handleKeyDown);
    return () => {
      currentInput.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, inputRef]);

  // Scroll item into view if arrowed
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const child = suggestionsRef.current.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      if (child) {
        child.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Render items
  function renderMovieItem(suggestion) {
    const idx = flattenedList.indexOf(suggestion);
    const isSelected = idx === selectedIndex;

    return (
      <div
        key={`movie-${suggestion.id}`}
        data-index={idx}
        className={`suggestion-item movie ${isSelected ? 'selected' : ''}`}
        onMouseEnter={() => setSelectedIndex(idx)}
        onClick={() => onSelect(suggestion)}
      >
        {suggestion.poster_path && (
          <img
            src={`https://image.tmdb.org/t/p/w92${suggestion.poster_path}`}
            alt={suggestion.title}
            className="suggestion-poster"
          />
        )}
        <div className="suggestion-details">
          <div className="suggestion-title">{suggestion.title}</div>
          <div className="suggestion-meta">
            {suggestion.year && <span className="suggestion-year">({suggestion.year})</span>}
            {suggestion.type === 'upcoming' ? (
              <span className="suggestion-upcoming-badge">Upcoming</span>
            ) : suggestion.rating && (
              <span className="suggestion-rating">
                <Star size={14} />
                {suggestion.rating}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderPersonItem(suggestion) {
    const idx = flattenedList.indexOf(suggestion);
    const isSelected = idx === selectedIndex;

    return (
      <div
        key={`person-${suggestion.id}-${suggestion.type}`}
        data-index={idx}
        className={`suggestion-item person ${isSelected ? 'selected' : ''}`}
        onMouseEnter={() => setSelectedIndex(idx)}
        onClick={() => onSelect(suggestion)}
      >
        <div className="suggestion-icon">
          {suggestion.type === 'Production Company' ? '🎬' : '👤'}
        </div>
        <div className="suggestion-details">
          <div className="suggestion-name">{suggestion.name}</div>
          <div className="suggestion-meta">
            <span className="suggestion-type">{suggestion.type}</span>
          </div>
        </div>
      </div>
    );
  }

  // ~~~~~~~~~~~~~ MAIN RENDER ~~~~~~~~~~~~~

  // not visible or no 2+ chars => no pop up
  if (!visible || !query || query.length < 2) return null;

  // if loading => shimmer skeleton matching two-column layout
  if (loading) {
    return (
      <div
        className="search-suggestions"
        style={{ '--suggestion-height': `${SUGGESTION_ITEM_HEIGHT}px` }}
      >
        <div className="search-suggestions-columns">
          <div className="movies-column">
            <div className="skeleton-header" />
            {[...Array(3)].map((_, idx) => (
              <div key={`m-${idx}`} className="skeleton-item shimmer">
                <div className="skeleton-poster" />
                <div className="skeleton-content">
                  <div className="skeleton-title" />
                  <div className="skeleton-subtitle" />
                </div>
              </div>
            ))}
          </div>
          <div className="people-column">
            <div className="skeleton-header" />
            {[...Array(3)].map((_, idx) => (
              <div key={`p-${idx}`} className="skeleton-item shimmer">
                <div className="skeleton-avatar" />
                <div className="skeleton-content">
                  <div className="skeleton-title" />
                  <div className="skeleton-subtitle" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // After loading, if the last fetched query matches the current query and no suggestions, show "No results found"
  if (!loading && lastFetchedQuery === query && allSuggestions.length === 0) {
    return (
      <div
        className="search-suggestions"
        style={{ '--suggestion-height': `${SUGGESTION_ITEM_HEIGHT}px` }}
      >
        <div className="no-suggestions">No results found</div>
      </div>
    );
  }

  // we have some results
  return (
    <div
      className="search-suggestions"
      ref={suggestionsRef}
      style={{ '--suggestion-height': `${SUGGESTION_ITEM_HEIGHT}px` }}
    >
      <div className="search-suggestions-columns">
        <div className="movies-column">
          <h4>Movies</h4>
          {movieSuggestions.map(m => renderMovieItem(m))}
        </div>
        <div className="people-column">
          <h4>People/Studios</h4>
          {peopleSuggestions.map(p => renderPersonItem(p))}
        </div>
      </div>
    </div>
  );

};

export default React.memo(SearchSuggestions);
